import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  addMedia,
  getMedia,
  getMediasLoading,
  selectAllMedias,
  selectCurrentPage,
  selectTotalCount,
  selectLocatedMediaId,
} from "../features/mediaSlice";
import Loading from "./loading";
import {
  getMediaPickerEmptyMessage,
  getMediaPickerTitle,
} from "../global/functions/mediaPickerHelper";
import { resolvePublicMediaUrl } from "../global/functions/imageHelper";
import { useTranslation } from "react-i18next";
import { useRestaurant } from "../hooks/RestaurantContext";

const apiUrl = process.env.REACT_APP_MEDIA_URL;
const PAGE_LIMIT = 12;

const isImageMedia = (item) => {
  const mime = item.mimeType || item.mimetype || "";
  if (mime.startsWith("video/")) return false;
  if (item.type === "carousel" || item.type === "video") return false;
  return Boolean(item.url);
};

const MediaPickerDialog = ({
  open,
  onClose,
  onSelect,
  targetType = "",
  title,
  initialMediaId,
  initialMediaUrl,
  acceptVideos = false,
  shared = false,
  restaurantIdOverride,
  standalone = false,
}) => {
  const { t } = useTranslation();
  const dialogTitle = title || getMediaPickerTitle(targetType);
  const emptyMessage = getMediaPickerEmptyMessage(targetType);
  const dispatch = useDispatch();
  const { restaurantId: contextRestaurantId } = useRestaurant();
  const ownerOnly = targetType === "Restaurant";
  const restaurantId = ownerOnly
    ? restaurantIdOverride || ""
    : standalone
      ? ""
      : restaurantIdOverride !== undefined
        ? restaurantIdOverride
        : contextRestaurantId;
  const mediaItems = useSelector(selectAllMedias);
  const loading = useSelector(getMediasLoading);
  const currentPage = useSelector(selectCurrentPage);
  const totalCount = useSelector(selectTotalCount);
  const locatedMediaId = useSelector(selectLocatedMediaId);
  const [selectedId, setSelectedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [scopeFilter, setScopeFilter] = useState(ownerOnly ? "restaurant" : "all");
  const [sessionItems, setSessionItems] = useState([]);
  const [brokenIds, setBrokenIds] = useState({});
  const uploadInputRef = useRef(null);
  const totalPages = Math.ceil(totalCount / PAGE_LIMIT) || 1;
  const uploadTypeByTarget = { Settings: "banner", Restaurant: "logo" };

  const fetchMedia = useCallback(
    (page = 1, locate, scope = scopeFilter) => {
      dispatch(
        getMedia({
          page,
          limit: PAGE_LIMIT,
          targetType,
          replace: true,
          locateId: locate?.locateId,
          locateUrl: locate?.locateUrl,
          restaurantId,
          scope: scope === "all" ? "" : scope,
        })
      );
    },
    [dispatch, targetType, restaurantId, scopeFilter]
  );

  useEffect(() => {
    if (!open) return;
    setSelectedId(initialMediaId || null);
    setScopeFilter(ownerOnly ? "restaurant" : "all");
    setSessionItems([]);
  }, [open, initialMediaId, ownerOnly]);

  useEffect(() => {
    if (!open || (ownerOnly && !restaurantId)) return;
    fetchMedia(
      1,
      ownerOnly || scopeFilter === "all"
        ? {
            locateId: initialMediaId,
            locateUrl: initialMediaId ? undefined : initialMediaUrl,
          }
        : undefined,
      ownerOnly ? "restaurant" : scopeFilter
    );
    // fetchMedia changes with scopeFilter; calling it here keeps the list on the chosen filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scopeFilter, targetType, restaurantId, initialMediaId, initialMediaUrl]);

  useEffect(() => {
    if (open && locatedMediaId) {
      setSelectedId(locatedMediaId);
    }
  }, [open, locatedMediaId]);

  const imageItems = (ownerOnly && !restaurantId ? sessionItems : mediaItems).filter(
    isImageMedia
  );

  const handleUploadFile = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;

    setUploading(true);
    try {
      const mediaType =
        file.type === "video/mp4"
          ? "video"
          : uploadTypeByTarget[targetType] || "image";
      const formData = new FormData();
      formData.append("type", mediaType);
      formData.append("files", file);
      if (targetType) {
        formData.append("targetType", targetType);
      }
      const uploadShared = ownerOnly ? false : standalone || !restaurantId ? true : shared;
      formData.append("shared", uploadShared ? "true" : "false");
      const saved = await dispatch(
        addMedia({ formData, type: mediaType, restaurantId })
      ).unwrap();
      const newMedia = Array.isArray(saved) ? saved[0] : saved;
      if (newMedia?._id) {
        setSelectedId(newMedia._id);
        if (ownerOnly && !restaurantId) {
          setSessionItems((prev) => [
            newMedia,
            ...prev.filter((item) => item._id !== newMedia._id),
          ]);
        } else {
          const nextScope = uploadShared ? "shared" : "restaurant";
          const locate = ownerOnly
            ? {
                locateId: initialMediaId,
                locateUrl: initialMediaId ? undefined : initialMediaUrl,
              }
            : undefined;
          if (scopeFilter === nextScope) fetchMedia(1, locate, nextScope);
          else setScopeFilter(nextScope);
        }
      }
    } catch (err) {
      toast.error(
        (typeof err === "string" ? err : err?.message) ||
          "Échec de l'envoi de l'image."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = () => {
    const selected = imageItems.find((item) => item._id === selectedId);
    if (selected) {
      onSelect({
        type: "existing",
        mediaId: selected._id,
        url: selected.url,
        filename: selected.filename,
      });
    }
    onClose();
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h5" component="span">{dialogTitle}</Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => uploadInputRef.current?.click()}
          disabled={uploading}
          startIcon={
            uploading ? (
              <CircularProgress size={16} />
            ) : (
              <FileUploadOutlinedIcon />
            )
          }
        >
          {t("fields.newImage")}
        </Button>
        <input
          ref={uploadInputRef}
          type="file"
          accept={acceptVideos ? ".jpg,.jpeg,.png,.gif,.mp4" : ".jpg,.jpeg,.png"}
          style={{ display: "none" }}
          onChange={handleUploadFile}
        />
      </DialogTitle>
      <DialogContent>
        {!standalone && !ownerOnly && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={scopeFilter}
          onChange={(_, value) => {
            if (value) setScopeFilter(value);
          }}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="all">{t("media.filterAll")}</ToggleButton>
          <ToggleButton value="shared">{t("media.filterShared")}</ToggleButton>
          <ToggleButton value="restaurant">{t("media.filterExclusive")}</ToggleButton>
        </ToggleButtonGroup>
        )}
        {loading && !uploading ? (
          <Loading />
        ) : imageItems.length === 0 ? (
          <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
            {emptyMessage}
          </Typography>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {imageItems.map((item) => {
              const isSelected = selectedId === item._id;
              const imageUrl = resolvePublicMediaUrl(item.url, apiUrl);
              const isBroken = Boolean(brokenIds[item._id]);

              return (
                <Grid item xs={6} sm={4} md={3} key={item._id}>
                  <Box
                    onClick={() => setSelectedId(item._id)}
                    sx={{
                      border: isSelected
                        ? "2px solid #1976d2"
                        : "2px solid transparent",
                      borderRadius: 2,
                      p: 1,
                      cursor: "pointer",
                      position: "relative",
                      transition: "border 0.2s",
                      boxShadow: isSelected ? "0 0 8px 0 #1976d255" : "none",
                    }}
                  >
                    {isBroken ? (
                      <Box
                        sx={{
                          width: "100%",
                          height: 100,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "action.hover",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" align="center">
                          {item.filename || "Image indisponible"}
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={item.filename || "media"}
                        onError={() =>
                          setBrokenIds((prev) => ({ ...prev, [item._id]: true }))
                        }
                        sx={{
                          width: "100%",
                          height: 100,
                          objectFit: "contain",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "action.hover",
                        }}
                      />
                    )}
                    {item.filename && (
                      <Typography
                        variant="caption"
                        align="center"
                        sx={{
                          mt: 0.5,
                          display: "-webkit-box",
                          WebkitBoxOrient: "vertical",
                          WebkitLineClamp: 3,
                          overflow: "hidden",
                          wordBreak: "break-word",
                        }}
                      >
                        {item.filename}
                      </Typography>
                    )}
                    {isSelected && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          background: "#fff",
                          borderRadius: "50%",
                          boxShadow: 1,
                          display: "flex",
                        }}
                      >
                        <CheckCircleIcon sx={{ color: "primary.main" }} />
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage || 1}
              onChange={(_, page) => fetchMedia(page)}
              color="primary"
              disabled={loading}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="info"
          disabled={!selectedId}
        >
          {t("fields.select")}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default MediaPickerDialog;
